import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { PageDataService } from '../../core/page-data.service';
import { I18nService } from '../../core/i18n.service';
import { SiteConfigService } from '../../core/site-config.service';
import { ResponsiveImageComponent } from '../../shared/responsive-image.component';
import { LocalDatePipe } from '../../shared/local-date.pipe';
import { BlogItem, BlogService } from './blog.service';

@Component({
  selector: 'app-blog-list-widget',
  imports: [LocalDatePipe, ResponsiveImageComponent, RouterLink],
  template: `
    <section
      class="blog-list-widget"
      [class.blog-list-widget--global]="variant() === 'globalEditorial'"
      [class.blog-list-widget--mystic]="variant() === 'mysticFortune'"
    >
      <div class="section-head">
        <p class="eyebrow">{{ i18n.t('blog') }}</p>
        <h1>{{ i18n.t('blogGuidesTitle') }}</h1>
      </div>

      <div class="blog-grid">
        @for (item of items(); track item.id; let itemIndex = $index) {
          <article class="blog-list-card">
            <div class="media">
              @if (item.imageUrl) {
                <app-responsive-image
                  [url]="item.imageUrl"
                  [mediaSizes]="item.imageSizes"
                  [alt]="item.title"
                  [priority]="itemIndex < 4"
                  displaySizes="(max-width: 760px) 88vw, (max-width: 1100px) 46vw, 360px"
                />
              }
            </div>
            <div class="content">
              <span>{{ (item.publishedAt || item.createdAt) | localDate: 'mediumDate' }} · {{ item.readingMinute || 1 }} dk</span>
              <h2>{{ item.title }}</h2>
              <p>{{ item.preview }}</p>
              <a [routerLink]="i18n.routePath('blogDetail', { slug: item.slug })">{{ i18n.t('blogRead') }}</a>
            </div>
          </article>
        } @empty {
          <article class="empty-card">
            <h2>{{ i18n.t('blogEmptyTitle') }}</h2>
            <p>{{ i18n.t('blogEmptyDescription') }}</p>
          </article>
        }
      </div>

      <nav class="pager">
        @if (page() > 1) {
          <a class="pager-action" [routerLink]="[]" [queryParams]="{ page: page() - 1 > 1 ? page() - 1 : null }">{{ i18n.t('previous') }}</a>
        } @else {
          <span class="pager-action is-disabled" aria-disabled="true">{{ i18n.t('previous') }}</span>
        }
        <span>{{ pageLabel(page()) }}</span>
        @if (nextEnabled()) {
          <a class="pager-action" [routerLink]="[]" [queryParams]="{ page: page() + 1 }">{{ i18n.t('next') }}</a>
        } @else {
          <span class="pager-action is-disabled" aria-disabled="true">{{ i18n.t('next') }}</span>
        }
      </nav>
    </section>
  `,
  styleUrl: './blog-widgets.scss'
})
export class BlogListWidgetComponent {
  private readonly blog = inject(BlogService);
  private readonly pageData = inject(PageDataService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly i18n = inject(I18nService);
  private readonly site = inject(SiteConfigService);
  protected readonly items = signal<BlogItem[]>([]);
  protected readonly page = signal(1);
  protected readonly hasMore = signal(false);
  protected readonly nextEnabled = signal(true);
  protected readonly loading = signal(false);
  protected readonly variant = computed(() => this.site.config().design.widgets['blogList'] ?? 'globalEditorial');
  private seededKey = '';
  private requestSerial = 0;
  private initialized = false;

  constructor() {
    const requestedPage = Math.max(1, Number(this.route.snapshot.queryParamMap.get('page') ?? 1));
    this.page.set(requestedPage);
    const seeded = this.getSeededResponse(this.pageData.data());
    if (seeded && seeded.page === requestedPage) {
      this.applySeeded(seeded);
    } else {
      void this.load(requestedPage);
    }
    this.initialized = true;
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      const nextPage = Math.max(1, Number(this.router.parseUrl(event.urlAfterRedirects).queryParams['page'] ?? 1));
      if (this.initialized && nextPage === this.page()) return;
      this.page.set(nextPage);
      this.seededKey = '';
      const seededPage = this.getSeededResponse(this.pageData.data());
      if (seededPage && seededPage.page === nextPage) {
        this.applySeeded(seededPage);
      } else {
        void this.load(nextPage);
      }
    });
  }

  protected pageLabel(value: number): string { return this.i18n.t('page').replace(':number', String(value)); }

  private async load(requestedPage = this.page(), forceApi = false): Promise<void> {
    const requestID = ++this.requestSerial;
    if (typeof window !== 'undefined') console.info('[blog-pager] load()', { requestedPage, forceApi, requestID });
    this.loading.set(true);
    try {
      const data = await this.blog.list(requestedPage, 9, !forceApi);
      if (typeof window !== 'undefined') console.info('[blog-pager] result()', { requestedPage, returnedPage: data.page, items: data.items.length, total: data.total, hasMore: data.hasMore });
      if (requestID !== this.requestSerial || this.page() !== requestedPage) return;
      this.items.set(data.items);
      this.hasMore.set(data.hasMore);
      this.nextEnabled.set(data.hasMore);
      this.seededKey = 'blogList';
    } catch {
      if (requestID !== this.requestSerial || this.page() !== requestedPage) return;
      this.items.set([]);
      this.hasMore.set(false);
      this.nextEnabled.set(false);
    } finally {
      if (requestID === this.requestSerial) this.loading.set(false);
    }
  }

  private applySeeded(seeded: { items: BlogItem[]; hasMore: boolean; page: number; limit: number; total: number }): void {
    this.items.set(seeded.items.map((item) => this.blog.normalizeItem(item)));
    this.hasMore.set(seeded.total > 0 ? seeded.page * seeded.limit < seeded.total : seeded.items.length >= seeded.limit && seeded.hasMore);
    this.nextEnabled.set(this.hasMore());
    this.seededKey = 'blogList';
  }

  private getSeededResponse(pageData: Record<string, unknown>): { items: BlogItem[]; hasMore: boolean; page: number; limit: number; total: number } | null {
    const seeded = this.isRecord(pageData['blogList']) ? pageData['blogList'] as Record<string, unknown> : null;
    const data = this.isRecord(seeded?.['data']) ? seeded['data'] as Record<string, unknown> : null;
    const items = Array.isArray(data?.['items']) ? data['items'] as BlogItem[] : null;
    if (!items?.length) {
      return null;
    }

    return {
      items,
      hasMore: Boolean(data?.['hasMore']),
      page: Math.max(1, Number(data?.['page'] ?? 1)),
      limit: Math.max(1, Number(data?.['limit'] ?? (items.length || 1))),
      total: Math.max(0, Number(data?.['total'] ?? 0))
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
